import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { buildMeta } from "@/seo/buildMeta";

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function upsertLink(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("link");
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function removeManaged(selector) {
  document.head.querySelectorAll(selector).forEach((node) => node.remove());
}

export default function SEO() {
  const location = useLocation();

  useEffect(() => {
    const meta = buildMeta(location.pathname);
    document.documentElement.lang = meta.lang;
    document.title = meta.title;

    upsertMeta('meta[name="description"]', { name: "description", content: meta.description });
    upsertMeta('meta[name="robots"]', { name: "robots", content: meta.robots });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: meta.title });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: meta.description });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: meta.ogType });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: meta.canonical });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: meta.image });
    if (meta.imageAlt) upsertMeta('meta[property="og:image:alt"]', { property: "og:image:alt", content: meta.imageAlt });
    else removeManaged('meta[property="og:image:alt"]');
    upsertMeta('meta[property="og:locale"]', { property: "og:locale", content: meta.ogLocale });
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: meta.title });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: meta.description });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: meta.image });
    if (meta.imageAlt) upsertMeta('meta[name="twitter:image:alt"]', { name: "twitter:image:alt", content: meta.imageAlt });
    else removeManaged('meta[name="twitter:image:alt"]');
    upsertLink('link[rel="canonical"]', { rel: "canonical", href: meta.canonical });
    upsertLink('link[rel="alternate"][type="application/rss+xml"]', { rel: "alternate", type: "application/rss+xml", title: "TGWE Podcast RSS Feed", href: meta.rssFeed });

    removeManaged("link[data-seo-alternate='true']");
    for (const alternate of meta.alternates) {
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", alternate.hreflang);
      link.setAttribute("href", alternate.href);
      link.setAttribute("data-seo-alternate", "true");
      document.head.appendChild(link);
    }

    removeManaged("meta[data-seo-og-locale-alternate='true']");
    for (const locale of meta.ogAlternateLocales) {
      const alternateLocale = document.createElement("meta");
      alternateLocale.setAttribute("property", "og:locale:alternate");
      alternateLocale.setAttribute("content", locale);
      alternateLocale.setAttribute("data-seo-og-locale-alternate", "true");
      document.head.appendChild(alternateLocale);
    }

    removeManaged("meta[data-seo-article='true']");
    if (meta.ogType === "article") {
      const articleMetas = [
        ["author", meta.author],
        ["published_time", meta.datePublished],
        ["modified_time", meta.dateModified],
        ["section", meta.articleSection],
        ...meta.tags.map((tag) => ["tag", tag])
      ];

      for (const [name, content] of articleMetas) {
        if (!content) continue;
        const element = document.createElement("meta");
        element.setAttribute("property", `article:${name}`);
        element.setAttribute("content", content);
        element.setAttribute("data-seo-article", "true");
        document.head.appendChild(element);
      }

      if (meta.author) {
        upsertMeta('meta[name="author"]', { name: "author", content: meta.author });
      }
    } else {
      removeManaged('meta[name="author"]');
    }

    const existingJsonLd = document.head.querySelector("script[data-seo-jsonld='true']");
    if (existingJsonLd) existingJsonLd.remove();
    const script = document.createElement("script");
    script.setAttribute("type", "application/ld+json");
    script.setAttribute("data-seo-jsonld", "true");
    script.textContent = JSON.stringify(meta.jsonLd);
    document.head.appendChild(script);
  }, [location.pathname]);

  return null;
}
